/**
 * Public rooms store — module-level cache with persistent Firebase subscription.
 *
 * Solves the "flicker on return" problem: when a user navigates from a room back
 * to the home page, the cached room list renders instantly instead of showing
 * skeleton loaders, then updates seamlessly when fresh data arrives.
 *
 * Architecture:
 * - Module-level state persists across React navigations within the SPA
 * - Firebase subscription is ref-counted: starts on first consumer, stops when
 *   all consumers unmount (with a 1s grace period for navigation transitions)
 * - Cached data is preserved even after unsubscribe, enabling instant renders
 * - Uses useSyncExternalStore for tear-safe React 18+ integration
 */

import { subscribeToPublicRooms, type PublicRoomData } from "@/lib/rtdb";
import { PUBLIC_ROOMS_DISPLAY_LIMIT } from "@/shared/constants";
import { useSyncExternalStore } from "react";

// ============================================================================
// Module-level state (persists across navigations within the SPA)
// ============================================================================

/** Grace period before tearing down Firebase subscription after last consumer unmounts.
 *  Allows for React StrictMode remounts and brief navigation transitions. */
const TEARDOWN_GRACE_MS = 1000;

interface PublicRoomsSnapshot {
  rooms: PublicRoomData[];
  loading: boolean;
}

// The snapshot object is replaced (not mutated) on every change so that
// useSyncExternalStore can detect updates via referential equality.
let currentSnapshot: PublicRoomsSnapshot = { rooms: [], loading: true };

let unsubFirebase: (() => void) | null = null;
let refCount = 0;
let teardownTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

// ============================================================================
// Internal helpers
// ============================================================================

function notifyListeners() {
  listeners.forEach((fn) => fn());
}

function startSubscription() {
  if (unsubFirebase) return; // Already active

  // If we have no cached data yet, mark as loading.
  // If we do have cached data (from a previous subscription), keep it visible
  // while the new subscription catches up — avoids skeleton flash.
  if (currentSnapshot.rooms.length === 0 && currentSnapshot.loading === false) {
    currentSnapshot = { rooms: [], loading: true };
    notifyListeners();
  }

  unsubFirebase = subscribeToPublicRooms(
    PUBLIC_ROOMS_DISPLAY_LIMIT,
    (rooms) => {
      currentSnapshot = { rooms, loading: false };
      notifyListeners();
    },
    (error) => {
      console.warn("[publicRoomsStore] Subscription error:", error);
      currentSnapshot = { ...currentSnapshot, loading: false };
      notifyListeners();
    }
  );
}

function stopSubscription() {
  if (unsubFirebase) {
    unsubFirebase();
    unsubFirebase = null;
  }
  // Intentionally keep currentSnapshot cached so the next mount renders instantly.
}

// ============================================================================
// useSyncExternalStore contract
// ============================================================================

/**
 * Subscribe a React listener. Ref-counts consumers and manages the Firebase
 * subscription lifecycle (start on first, stop on last with grace period).
 */
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  refCount++;

  // Cancel any pending teardown — a new consumer appeared.
  if (teardownTimer) {
    clearTimeout(teardownTimer);
    teardownTimer = null;
  }

  startSubscription();

  return () => {
    listeners.delete(listener);
    refCount--;

    if (refCount <= 0) {
      refCount = 0;
      // Delay teardown to survive React StrictMode and brief navigation hops.
      teardownTimer = setTimeout(stopSubscription, TEARDOWN_GRACE_MS);
    }
  };
}

function getSnapshot(): PublicRoomsSnapshot {
  return currentSnapshot;
}

// SSR / server snapshot — no data available on the server.
const serverSnapshot: PublicRoomsSnapshot = { rooms: [], loading: true };

function getServerSnapshot(): PublicRoomsSnapshot {
  return serverSnapshot;
}

// ============================================================================
// React hook
// ============================================================================

/**
 * Subscribe to public rooms with cross-navigation caching.
 *
 * On first call, shows loading state while Firebase delivers data.
 * On subsequent calls (e.g. returning from a room), returns cached data
 * immediately and updates seamlessly when fresh data arrives.
 *
 * @returns `{ rooms, loading }` — the current public rooms list and loading state.
 */
export function usePublicRooms(): PublicRoomsSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
